import * as THREE from "three";
import type { Line2 } from "three/examples/jsm/lines/Line2.js";
import type { LineMaterial } from "three/examples/jsm/lines/LineMaterial.js";
import type { Palette } from "@designboticteam/core";
import { Polygon } from "@designboticteam/workspace-3d";
import { createOutlineLine, createOutlineMaterial, OUTLINE_DASH_PATTERN_LENGTH, OUTLINE_DASH_REFERENCE_ZOOM, OUTLINE_DASH_SPEED } from "./utils";

type ParcelZoneSource = "pog" | "mpzp" | "obszar_uzupelnienia_zabudowy";

type ParcelZoneSceneGeometry = {
	color: string;
	symbol: string;
	source: ParcelZoneSource;
	rings: Array<Array<THREE.Vector3>>;
};

type ParcelSceneGeometry = {
	outline: Array<Array<THREE.Vector3>>;
	zones: Array<ParcelZoneSceneGeometry>;
};

type ParcelShapeParams = {
	geometry: ParcelSceneGeometry;
	palette: Palette;
	showZones?: boolean;
	outlineColor?: THREE.ColorRepresentation;
	isOutlineDashed?: boolean;
};

const OUTLINE_FILL_OPACITY = 0.08;
const ZONE_FILL_OPACITY = 0.4;

class ParcelShape {
	public readonly sceneObject = new THREE.Group();

	private readonly outlinePolygons: Array<Polygon> = [];
	private readonly outlineLines: Array<Line2> = [];
	private readonly outlineMaterial: LineMaterial;
	private readonly zonePolygonsByZoneIndex = new Map<number, Array<Polygon>>();
	private readonly zoneSourceByZoneIndex = new Map<number, ParcelZoneSource>();

	private dashOffset = 0;
	private readonly isZoneSplitVisibleBySource: Record<ParcelZoneSource, boolean> = { pog: false, mpzp: false, obszar_uzupelnienia_zabudowy: false };
	private highlightedZoneIndex: number | null = null;

	constructor({ geometry, palette, showZones = true, outlineColor, isOutlineDashed = true }: ParcelShapeParams) {
		const color = outlineColor ?? palette.main.primary;

		this.outlineMaterial = createOutlineMaterial({ color, isDashed: isOutlineDashed });

		this.buildOutline(geometry.outline, color);

		if (showZones === true) {
			this.buildZones(geometry.zones, palette);
		}
	}

	/** */
	public dispose(): void {
		for (const polygon of this.outlinePolygons) {
			polygon.dispose();
		}

		for (const line of this.outlineLines) {
			line.geometry.dispose();
		}

		this.outlineMaterial.dispose();

		for (const polygons of this.zonePolygonsByZoneIndex.values()) {
			for (const polygon of polygons) {
				polygon.dispose();
			}
		}

		this.sceneObject.removeFromParent();
		this.sceneObject.clear();

		this.outlinePolygons.length = 0;
		this.outlineLines.length = 0;

		this.zonePolygonsByZoneIndex.clear();
	}

	/** */
	private buildOutline(rings: Array<Array<THREE.Vector3>>, color: THREE.ColorRepresentation): void {
		for (const points of rings) {
			const polygon = new Polygon({
				lineColor: color,
				fillColor: color,
				fillOpacity: OUTLINE_FILL_OPACITY,
			});
			polygon.setPoints({ points });
			polygon.finish();
			polygon.line.visible = false;

			const line = createOutlineLine({ points, material: this.outlineMaterial });

			this.outlinePolygons.push(polygon);
			this.outlineLines.push(line);
			this.sceneObject.add(polygon.sceneObject);
			this.sceneObject.add(line);
		}
	}

	/** */
	private buildZones(zones: Array<ParcelZoneSceneGeometry>, palette: Palette): void {
		zones.forEach(({ color, symbol, source, rings }, zoneIndex) => {
			const polygons: Array<Polygon> = [];

			for (const points of rings) {
				const polygon = new Polygon({
					label: symbol === "" ? undefined : symbol,
					// MPZP zone colours are often near-white — a black pill keeps those labels readable.
					labelColor: source === "mpzp" ? palette.neutral.black : color,
					// The line is what split mode shows — black for contrast against the basemap.
					lineColor: palette.neutral.black,
					fillColor: color,
					fillOpacity: ZONE_FILL_OPACITY,
				});
				polygon.setPoints({ points });
				polygon.finish();
				// Zones stay hidden until the split toggle or a panel-row hover reveals them.
				polygon.sceneObject.visible = false;

				polygons.push(polygon);
				this.sceneObject.add(polygon.sceneObject);
			}

			this.zonePolygonsByZoneIndex.set(zoneIndex, polygons);
			this.zoneSourceByZoneIndex.set(zoneIndex, source);
		});
	}

	/** */
	public advanceOutlineDashes({ deltaSeconds, mapZoom }: { deltaSeconds: number; mapZoom: number }): void {
		// Negative so the dashes travel forwards along the ring, wrapped to one period to keep float
		// precision (the shader's `mod` handles the negative value). Metres per pixel halve every zoom step.
		this.dashOffset = (this.dashOffset - OUTLINE_DASH_SPEED * deltaSeconds) % OUTLINE_DASH_PATTERN_LENGTH;
		this.outlineMaterial.dashOffset = this.dashOffset;
		this.outlineMaterial.dashScale = 2 ** (mapZoom - OUTLINE_DASH_REFERENCE_ZOOM);
	}

	/** */
	public setHighlightedZoneIndex(zoneIndex: number | null): void {
		this.highlightedZoneIndex = zoneIndex;
		this.applyZoneVisibility({ updateLabels: false });
	}

	/** */
	public setZoneSplitVisible(source: ParcelZoneSource, isVisible: boolean): void {
		this.isZoneSplitVisibleBySource[source] = isVisible;
		this.applyZoneVisibility({ updateLabels: true });
	}

	/** */
	private applyZoneVisibility({ updateLabels }: { updateLabels: boolean }): void {
		for (const [zoneIndex, polygons] of this.zonePolygonsByZoneIndex) {
			const source = this.zoneSourceByZoneIndex.get(zoneIndex) ?? "pog";
			const isSplitVisible = this.isZoneSplitVisibleBySource[source];
			const isHighlighted = zoneIndex === this.highlightedZoneIndex;

			for (const polygon of polygons) {
				if (updateLabels === true) {
					polygon.setNameLabelsVisibility({ isVisible: isSplitVisible });
				}

				polygon.sceneObject.visible = isSplitVisible === true || isHighlighted === true;
				polygon.fill.visible = isHighlighted === true;
			}
		}
	}
}

export { type ParcelSceneGeometry, ParcelShape, type ParcelShapeParams, type ParcelZoneSceneGeometry, type ParcelZoneSource };
